/**
 * React Query hooks for the contact CSV import wizard (ADR-141 §4 import
 * lane).
 *
 * Flow: dry-run (synchronous preview, no writes) → commit (202 + run id)
 * → poll the run until it reaches a terminal status. The wizard
 * invalidates the contacts + tags scopes when the run completes — the
 * commit task creates contacts/tags server-side while the poll is in
 * flight, so invalidating at mutation time would be too early.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  commitContactImport,
  dryRunContactImport,
  getContactImportRun,
  type ContactImportDryRunResponse,
  type ContactImportRun,
  type ImportColumnMapping,
} from '../../services/contacts.service';

export const contactImportKeys = {
  all: ['contactImport'] as const,
  run: (runId: number) => [...contactImportKeys.all, 'run', runId] as const,
};

export interface ImportRequestInput {
  file: File;
  mapping: ImportColumnMapping;
}

export function useContactImportDryRun() {
  return useMutation<ContactImportDryRunResponse, Error, ImportRequestInput>({
    mutationFn: ({ file, mapping }) => dryRunContactImport(file, mapping),
  });
}

export function useContactImportCommit() {
  return useMutation<ContactImportRun, Error, ImportRequestInput>({
    mutationFn: ({ file, mapping }) => commitContactImport(file, mapping),
  });
}

export function isTerminalRunStatus(
  status: ContactImportRun['status'] | undefined,
): boolean {
  return status === 'complete' || status === 'failed';
}

const RUN_POLL_INTERVAL_MS = 2000;

/**
 * Poll a committed run every 2s until it reaches `complete`/`failed`.
 * Pass `null` before a commit has been accepted (query stays disabled).
 */
export function useContactImportRun(runId: number | null) {
  return useQuery({
    queryKey: contactImportKeys.run(runId ?? -1),
    queryFn: () => getContactImportRun(runId as number),
    enabled: runId != null,
    refetchInterval: (query) =>
      isTerminalRunStatus(query.state.data?.status)
        ? false
        : RUN_POLL_INTERVAL_MS,
  });
}
